import React, { useState } from "react";
import "../../../css/ViewStationModal.css";
import api from "../../../api/axiosConfig"; // <-- ADDED
import ConfirmationModal from "./ConfirmationModal"; // <-- ADDED
import { showToast } from '../../Shared/Toaster';


// Re-using the Station interface shape from your table component
interface Station {
  id: string; // <-- Note: Using 'id' as per your interface
  type: string;
  description?: string;
  createdOn: string;
  disabledOn?: string;
  status: "active" | "inactive";
  roleName: string;
  subrole: string;
  subroleName: string;
}

interface ViewStationModalProps {
  isOpen: boolean;
  onClose: () => void;
  station: Station | null; // The station to display, or null
  onEdit: () => void;
  onUpdateSuccess: () => void; // <-- ADDED
}

const ViewStationModal: React.FC<ViewStationModalProps> = ({
  isOpen,
  onClose,
  station,
  onEdit,
  onUpdateSuccess, // <-- ADDED
}) => {
  // <-- ADDED STATE -->
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);

  // Don't render anything if the modal is not open or no station is selected
  if (!isOpen || !station) {
    return null;
  }

  // Determine current status
  const isActive = station.status === "active";

  // <-- ADDED: Handler for the disable/enable confirmation -->
  const handleDisableConfirm = async () => {
    if (!station) return;

    setIsDisabling(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) {
        showToast("Authentication error: No access token found.", 'error');
        setIsDisabling(false);
        return;
      }

      // Use api.center_api based on your AddStationModal example
      await api.center_api.patch(
        `/api/v1/stations/toggle-active`,
        { stationId: station.id }, // Send stationId in the body
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      showToast(`Station ${isActive ? "disabled" : "enabled"} successfully!`, 'success');

      onUpdateSuccess(); // Call the success prop to refresh parent
      setShowDisableConfirm(false);
      onClose(); // Close the main modal after action
    } catch (error) {
      console.error("Failed to toggle station status:", error);
      showToast("Error: Could not update the station status. Please try again.", 'error');
    } finally {
      setIsDisabling(false);
    }
  };

  return (
    // <-- ADDED: React.Fragment wrapper -->
    <React.Fragment>
      <div className="view-station-modal-overlay" onClick={onClose}>
        <div
          className="view-station-modal-content" onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="view-station-modal-header">
            <h2>View Station</h2>
            <button className="global-close-btn" onClick={onClose}>×</button>
          </div>

          {/* Modal Body */}
          <div className="view-station-modal-body">
            <div className="view-station-detail-item">
              <span className="view-station-detail-label">Station Name:</span>
              <span className="view-station-detail-value">{station.type}</span>
            </div>
            <div className="view-station-detail-item">
              <span className="view-station-detail-label">Description:</span>
              <span className="view-station-detail-value">
                {station.description || "N/A"}
              </span>
            </div>
            <div className="view-station-detail-item">
              <span className="view-station-detail-label">Assigned Role:</span>
              <span className="view-station-detail-value">
                {station.roleName}
              </span>
            </div>
            <div className="view-station-detail-item">
              <span className="view-station-detail-label">Assign Sub-Role:</span>
              <span className="view-station-detail-value">
                {station.subroleName || "N/A"}
              </span>
            </div>
            <div className="view-station-detail-item">
              <span className="view-station-detail-label">Status:</span>
              <span className="view-station-detail-value">{station.status}</span>
            </div>
            <div className="view-station-detail-item">
              <span className="view-station-detail-label">Created On:</span>
              <span className="view-station-detail-value">
                {station.createdOn}
              </span>
            </div>
            <div className="view-station-detail-item">
              <span className="view-station-detail-label">Disabled On:</span>
              <span className="view-station-detail-value">
                {station.disabledOn || "-"}
              </span>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="view-station-modal-footer">
            <button className="view-station-edit-btn" onClick={onEdit}>
              Edit
            </button>
            {/* --- MODIFIED BUTTON --- */}
            <button
              className={
                isActive
                  ? "view-station-disable-btn"
                  : "view-station-edit-btn" // Use disable or edit style
              }
              onClick={() => setShowDisableConfirm(true)}
              disabled={isDisabling}
            >
              {isDisabling
                ? isActive
                  ? "Disabling..."
                  : "Enabling..."
                : isActive
                ? "Disable"
                : "Enable"}
            </button>
          </div>
        </div>
      </div>

      {/* --- ADDED: Conditional Confirmation Modal --- */}
      {showDisableConfirm && (
        <ConfirmationModal
          message={`Are you sure you want to ${
            isActive ? "disable" : "enable"
          } this station?`}
          onConfirm={handleDisableConfirm}
          onCancel={() => setShowDisableConfirm(false)}
        />
      )}
    </React.Fragment>
  );
};

export default ViewStationModal;